import { Tag } from 'antd';
import { RcFile, UploadProps } from 'antd/es/upload';
import { AxiosError } from 'axios';
import { FC, memo, useCallback, useState } from 'react';

import { AppMessage, AppModal } from '@/common/components';
import { AppModalControlProps } from '@/common/interfaces';
import { messageOnError } from '@/common/utils';

import { useImportArchiveSitesMutation } from '@/modules/Sites';

import './SitesUploadArchive.scss';
import { SitesUploadArchiveView } from './SitesUploadArchiveView';

interface Props {
  modalControls: AppModalControlProps;
  siteId: string;
}

export const SitesUploadArchiveContainer: FC<Props> = memo(({ siteId, modalControls }) => {
  const appMessageApi = AppMessage.useControls();
  const { close } = modalControls;
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [showResultSuccess, setShowResultSuccess] = useState(false);

  const { mutateAsync } = useImportArchiveSitesMutation(siteId)();

  const uploaderProps: UploadProps = {
    name: 'archive',
    multiple: false,
    accept: '.zip',
    listType: 'picture',
    beforeUpload: (file: RcFile) => {
      const isLowerThan1gb = file.size / 1024 / 1024 / 1024 < 1;
      const isNotEmpty = file.size > 0;

      if (!isLowerThan1gb) {
        appMessageApi.error('Файл має бути менше 1 ГБ');
      }

      return isLowerThan1gb && isNotEmpty;
    },
    // className: 'upload-list-inline',
    customRequest: async ({ file, onSuccess, onError, onProgress, }) => {
      try {
        await mutateAsync({
          archive: file as RcFile,
          onUploadProgress: (event) => {
            if (event && event?.loaded && event?.total) {
              onProgress && onProgress({ percent: (event.loaded / event.total) * 100 });
            }
          }
        });

        onSuccess && onSuccess(null);
      } catch (err) {
        onError && onError(err instanceof AxiosError ? { ...err } : err as Error);
      }
    },
    progress: {
      format: percent => `${ percent?.toFixed(1) }%`,
      strokeWidth: 6,
    },
    onChange(info) {
      const { status, error, } = info.file;
      if (status === 'uploading') {
        setIsFileUploading(true);
      } else if (status === 'done') {
        setIsFileUploading(false);
        handleSuccessUpload();
        appMessageApi.success(
          <>
            Aрхів <Tag>{ info.file.name }</Tag> завантажено
          </>
        );
      } else if (status === 'error') {
        setIsFileUploading(false);


        if (error) {
          messageOnError(error, appMessageApi);
        } else {
          appMessageApi.error(
            <>
              Aрхів <Tag>{ info.file.name }</Tag> не вдалось завантажити
            </>
          );
        }
      }
    }
  };

  const handleSuccessUpload = useCallback((): void => {
    setShowResultSuccess(true);

    setTimeout(() => {
      close();
      setShowResultSuccess(false);
    }, 800);
  }, []);

  return (
    <AppModal
      title="Завантажити сайт з архіву"
      width={ 900 }
      maskClosable={ !isFileUploading }
      closable={ !isFileUploading }
      styles={{ body: { minHeight: 300 } }}
      footer={ false }
      controls={ modalControls }
    >
      <SitesUploadArchiveView
        isFileUploading={ isFileUploading }
        showResultSuccess={ showResultSuccess }
        uploaderProps={ uploaderProps }
      />
    </AppModal>
  );
});
